import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    FaRegFileAlt,
    FaUser,
    FaUtensils,
    FaWallet,
    FaUserFriends,
    FaExchangeAlt,
    FaStore
} from "react-icons/fa";
import {
    MdOutlineHeadsetMic,
    MdSettings,
    MdLogout,
    MdMenu,
    MdClose,
    MdKeyboardArrowDown,
    MdRestaurantMenu,
    MdSpaceDashboard,
    MdApartment,
    MdPendingActions,
    MdOutlineAssignment,
    MdOutlineInventory2,
    MdOutlineUpdate,
    MdOutlineHomeWork,
    MdOutlineFoodBank,
    MdInventory2
} from "react-icons/md";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import { capitalizeFirstLetter, getRole, getUserType, hasPermission } from "../../helpers";
import Config from "../../Config";
import { HiOutlineChartBar, HiOutlineFolderOpen, HiOutlineUserGroup, HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { TbTransferIn } from "react-icons/tb";
import { BiPurchaseTag } from "react-icons/bi";
import { GrStorage } from "react-icons/gr";
import { IoBasketOutline, IoRestaurantOutline } from "react-icons/io5";


const SideNabar = ({ onLogoutClick }) => {
    const { pathname } = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const user = useSelector((state) => state.UserReducer.user);

    const [openSections, setOpenSections] = useState({
        property: false,
        money: false,
        tenants: false,
        quickAction: false,
        landlords: false,
        sales: false,
        food: false,
        foodInventory: false,
    });

    useEffect(() => {
        setIsMobileOpen(false);
    }, [pathname]);

    const isPayoutEnabled = useSelector((state) => state.IsPayoutEnabledReducer.isPayoutEnabled);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobileOpen && !event.target.closest('.sidebar-container') && !event.target.closest('.mobile-menu-button')) {
                setIsMobileOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobileOpen]);

    useEffect(() => {
        if (isMobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileOpen]);


    // Regular menu - only dashboard
    const regularMenuItems = [
        {
            type: "link",
            href: "/dashboard",
            icon: MdSpaceDashboard,
            label: "Dashboard",
            active: pathname === "/dashboard"
        },

        hasPermission("MANANGE_COMPLAINTS", user) && {
            type: "link",
            href: "/complaints",
            icon: MdOutlineHeadsetMic,
            label: "Complaints",
            active: pathname === "/complaints"
        },
        // {
        //     type: "link",
        //     href: "/property-list",
        //     icon: MdApartment,
        //     label: "Properties",
        //     active: pathname === "/property-list"
        // },
        hasPermission("MANAGE_FOOD", user) && {
            type: "link",
            href: "/food-menu",
            icon: FaUtensils,
            label: "Food Menu",
            active: pathname === "/food-menu"
        },
        hasPermission("MANAGE_FOOD", user) && {
            type: "link",
            href: "/meal-summary",
            icon: HiOutlineChartBar,
            label: "Meal Summary",
            active: pathname === "/meal-summary"
        },
        isPayoutEnabled === Config.PAYOUT_ENABLED.YES && {
            type: "dropdown",
            key: "payouts",
            icon: FaWallet,
            label: "Payouts",
            isOpen: openSections.payouts,
            onToggle: () => toggleSection("payouts"),
            items: [
                {
                    href: "/payouts/beneficiaries",
                    icon: FaUserFriends,
                    label: "Beneficiaries",
                    active: pathname === "/payouts/beneficiaries"
                },
                {
                    href: "/payouts/transfers",
                    icon: FaExchangeAlt,
                    label: "Transfers",
                    active: pathname === "/payouts/transfers"
                },
            ]
        },

        hasPermission("MANAGE_INVENTORY", user) && {
            type: "dropdown",
            key: "foodInventory",
            icon: MdInventory2,
            label: "Inventory",
            isOpen: openSections.foodInventory,
            onToggle: () => toggleSection("foodInventory"),
            items: [
                {
                    href: "/stock-transfers",
                    icon: TbTransferIn,
                    label: "Transfers",
                    active: pathname === "/stock-transfers"
                },
                {
                    href: "/purchase-inventory",
                    icon: BiPurchaseTag,
                    label: "Purchases",
                    active: pathname === "/purchase-inventory"
                },
                {
                    href: "/inventory-stock",
                    icon: GrStorage,
                    label: "Stock",
                    active: pathname === "/inventory-stock"
                },
                {
                    href: "/food-items",
                    icon: IoBasketOutline,
                    label: "Items",
                    active: pathname === "/food-items"
                },
                {
                    href: "/kitchens",
                    icon: IoRestaurantOutline,
                    label: "Kitchens",
                    active: pathname === "/kitchens"
                },
            ]
        },
        hasPermission("MANAGE_VENDOR", user) && {
            type: "link",
            href: "/vendors",
            icon: FaStore,
            label: "Vendors",
            active: pathname === "/vendors"
        },
        // {
        //     type: "link",
        //     href: "/reports",
        //     icon: FaRegFileAlt,
        //     label: "Reports",
        //     active: pathname === "/reports"
        // },
        // {
        //     type: "link",
        //     href: "/settings",
        //     icon: MdSettings,
        //     label: "Settings",
        //     active: pathname === "/settings"
        // },
    ];

    const cleanMenu = (menus) => {
        return menus.filter(Boolean);
    };

    useEffect(() => {
        const newOpenSections = {
            property: false,
            money: false,
            tenants: false,
            quickAction: false,
            landlords: false,
            sales: false,
            food: false,
            foodInventory: false,
            payouts: false
        };

        if (pathname === '/leads' || pathname === '/leads/followups' || pathname === '/leads/visits') {
            newOpenSections.sales = true;
        } else if (pathname === '/inventory-stock' || pathname === '/stock-transfers' || pathname === '/transfered-items' || pathname === '/purchase-inventory' || pathname === '/food-items' || pathname === '/kitchens') {
            newOpenSections.foodInventory = true;
        }
        else if (pathname === '/payouts/beneficiaries' || pathname === '/payouts/transfers' || pathname === '/payouts/staff-wallet') {
            newOpenSections.payouts = true;
        }

        setOpenSections(newOpenSections);
    }, [pathname]);

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleMobileSidebar = () => {
        setIsMobileOpen(!isMobileOpen);
    };

    const safeMenuItems = cleanMenu(regularMenuItems);

    const MenuButton = ({ children, isActive, onClick, asLink = false, href = "#", className = "" }) => {
        if (asLink) {
            return (
                <MenuLink
                    to={href}
                    $isActive={isActive}
                    className={className}
                >
                    {children}
                </MenuLink>
            );
        }

        return (
            <MenuButtonWrapper
                onClick={onClick}
                $isActive={isActive}
                className={className}
            >
                {children}
            </MenuButtonWrapper>
        );
    };

    const renderMenuItem = (item) => {
        if (item.type === "link") {
            return (
                <li key={item.href}>
                    <MenuButton
                        asLink
                        href={item.href}
                        isActive={item.active}
                    >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <MenuItemLabel
                            $isCollapsed={isCollapsed}
                            $isMobileOpen={isMobileOpen}
                        >
                            {item.label}
                        </MenuItemLabel>
                    </MenuButton>
                </li>
            );
        }
        else if (item.type === "dropdown") {
            return (
                <li key={item.key} className="mb-1">
                    <MenuButton
                        onClick={item.onToggle}
                        isActive={item.active}
                        className="justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            <MenuItemLabel
                                $isCollapsed={isCollapsed}
                                $isMobileOpen={isMobileOpen}
                            >
                                {item.label}
                            </MenuItemLabel>
                        </div>
                        <DropdownIcon
                            $isOpen={item.isOpen}
                            $isCollapsed={isCollapsed}
                            $isMobileOpen={isMobileOpen}
                        />
                    </MenuButton>
                    {item.isOpen && (
                        <DropdownMenu
                            $isCollapsed={isCollapsed}
                            $isMobileOpen={isMobileOpen}
                        >
                            {item.items.map((subItem) => (
                                <li key={subItem.href}>
                                    <MenuButton
                                        asLink
                                        href={subItem.href}
                                        isActive={subItem.active}
                                        className="pl-3"
                                    >
                                        <subItem.icon className="h-4 w-4 flex-shrink-0" />
                                        <span>{subItem.label}</span>
                                    </MenuButton>
                                </li>
                            ))}
                        </DropdownMenu>
                    )}
                </li>
            );
        }
        return null;
    };

    const userRole = getRole(user?.userRole);
    const userType = getUserType(user?.userType);

    const SidebarFooter = () => {
        return (
            <FooterContainer>
                <div className="flex justify-between p-1.5 rounded-lg bg-gray-50">
                    <UserInfoContainer>
                        <UserAvatar>
                            <FaUser className="h-4 w-4" />
                        </UserAvatar>
                        {(!isCollapsed || isMobileOpen) && (
                            <UserDetails>
                                <UserName>{user?.name ? capitalizeFirstLetter(user?.name) : "User"}</UserName>
                                {user.userRoler ?
                                    <UserRole>{userRole}</UserRole> :
                                    <UserRole>{userType}</UserRole>}
                            </UserDetails>
                        )}
                    </UserInfoContainer>

                    <button className="flex justify-end items-center w-1/4" onClick={onLogoutClick}>
                        <MdLogout className="h-4 w-4 flex-shrink-0 text-rose-500" title="Logout" />
                    </button>
                </div>
            </FooterContainer>
        );
    };

    return (
        <>
            <MobileMenuButton
                className="mobile-menu-button"
                onClick={toggleMobileSidebar}
            >
                {isMobileOpen ? <MdClose className="h-5 w-5" /> : <MdMenu className="h-5 w-5" />}
            </MobileMenuButton>

            {isMobileOpen && (
                <MobileOverlay onClick={() => setIsMobileOpen(false)} />
            )}

            <SidebarContainer
                $isCollapsed={isCollapsed}
                $isMobileOpen={isMobileOpen}
            >
                <SidebarHeader>
                    <div className="flex items-center">
                        {(!isCollapsed || isMobileOpen) && (
                            <div className="flex items-center gap-3">
                                <Logo src={Images.Logo} alt="Kipinn Logo" />
                                <div className="hidden md:flex items-center gap-3">
                                    <div className="h-4 w-px bg-gray-300" />
                                    <span className="text-sm font-medium text-gray-700">
                                        PG & Coliving
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <MobileCloseButton
                        onClick={() => setIsMobileOpen(false)}
                    >
                        <MdClose className="h-4 w-4" />
                    </MobileCloseButton>
                </SidebarHeader>

                <SidebarContent>
                    <SidebarNav>
                        <SidebarMenu>
                            {safeMenuItems.map(renderMenuItem)}
                        </SidebarMenu>
                    </SidebarNav>
                </SidebarContent>
                <SidebarFooter />
            </SidebarContainer>
        </>
    );
};

// Styled Components
const MobileMenuButton = tw.button`lg:hidden fixed top-3 left-4 z-10 p-2 bg-white rounded-lg shadow-lg border border-gray-200 text-gray-600 hover:bg-gray-100`;
const MobileOverlay = tw.div`lg:hidden fixed inset-0 bg-black bg-opacity-50 z-5 transition-opacity duration-300`;
const SidebarContainer = tw.div`no-scrollbar sidebar-container flex flex-col bg-white transition-all duration-300 h-screen z-10 ${props => props.$isCollapsed ? 'w-16' : 'w-60'} lg:static lg:translate-x-0 lg:shadow-none fixed top-0 left-0 ${props => props.$isMobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'} ${props => props.$isCollapsed && !props.$isMobileOpen ? 'lg:w-16' : ''}`;
const SidebarHeader = tw.div`sidebar-header flex items-center justify-between p-4 min-h-16`;
const Logo = tw.img`w-20`;
const MobileCloseButton = tw.button`lg:hidden flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-transparent text-gray-600 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-900`;
const SidebarContent = tw.div`sidebar-content flex-1 overflow-y-auto p-2 no-scrollbar`;
const SidebarNav = tw.nav`sidebar-nav h-full`;
const SidebarMenu = tw.ul`sidebar-menu list-none p-0 m-0`;
const FooterContainer = tw.div`p-4 border-t border-gray-200`;
const UserInfoContainer = tw.div`flex items-center gap-3 w-3/4`;
const UserAvatar = tw.div`flex items-center justify-center w-8 h-8 rounded-full bg-primary-color text-white flex-shrink-0`;
const UserDetails = tw.div`flex flex-col min-w-0`;
const UserName = tw.span`text-sm font-medium text-gray-900 truncate`;
const UserRole = tw.span`text-xs text-gray-500 truncate`;
const MenuButtonWrapper = tw.button`flex items-center gap-3 w-full p-3 text-base rounded-md transition-all duration-200 hover:text-primary-color ${props => props.$isActive ? 'text-primary-color' : 'text-gray-600'} ${props => props.className || ''}`;
const MenuLink = tw(Link)`flex items-center gap-3 w-full p-3 text-base rounded-md transition-all duration-200 hover:text-primary-color ${props => props.$isActive ? 'text-primary-color' : 'text-gray-600'} ${props => props.className || ''}`;
const MenuItemLabel = tw.span`${props => props.$isCollapsed && !props.$isMobileOpen ? 'lg:hidden' : ''} ${props => props.$isMobileOpen ? 'block' : 'lg:block'}`;
const DropdownIcon = tw(MdKeyboardArrowDown)`h-4 w-4 transition-transform ${props => props.$isOpen ? "rotate-180" : ""} ${props => props.$isCollapsed && !props.$isMobileOpen ? 'lg:hidden' : ''} ${props => props.$isMobileOpen ? 'block' : 'lg:block'}`;
const DropdownMenu = tw.ul`ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3 ${props => props.$isCollapsed && !props.$isMobileOpen ? 'lg:hidden' : ''} ${props => props.$isMobileOpen ? 'block' : 'lg:block'}`;

export default SideNabar;